import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';

import TableDataContent from './TableDataContent';
import NoData from '../alert/NoData';
import { TableRowDataStyle, TableRowFooterStyle } from './tableStyle';

import {
  TABLE_ROWS,
  TABLE_HEAD_HEIGHT,
  TABLE_ROWS_HEIGHT_TWO,
  TABLE_ROWS_HEIGHT_SINGLE
} from './constants';

import theme from '../../theme';


const CustomTableHeader = ({ userAuth, tableColumnsDefined, sort, order, createSortHandler }) => {

  return (
    <>
      {
        tableColumnsDefined.tableHeader.map(header => {

          if (userAuth && header.allow && !header.allow.includes(userAuth.role)) {
            return null;
          }

          return (
            <TableCell
              key={`header-${header.id}`}
              align="center"
            >
              {
                header.sort &&
                <TableSortLabel
                  active={sort === header.id}
                  direction={sort === header.id ? order : 'asc'}
                  onClick={createSortHandler(header.id)}
                >
                  {header.align !== 'right' && <>{header.name}</>}
                  {header.tooltip}
                  {header.align === 'right' && <>{header.name}</>}
                </TableSortLabel>
              }

              {
                !header.sort &&
                <span>
                  {header.name}
                  {header.tooltip}
                </span>
              }
            </TableCell>
          )
        })
      }
    </>
  )
}

const CustomTableDataCell = ({ userAuth, tableColumnsDefined, tableData, handleTableAction, handleOtherInfo }) => {

  return (
    <>
      {
        tableData.map((row, index) => {
          // console.log('row', row);

          return (
            <TableRowDataStyle
              hover
              key={`${row[tableColumnsDefined.key]}-${index}`}
            >
              {
                tableColumnsDefined.tableHeader.map((header, index) => {
                  const width = header.width ? header.width : '120px';

                  if (userAuth && header.allow && !header.allow.includes(userAuth.role)) {
                    return null;
                  }
                  return (
                    <TableCell
                      key={`${row[tableColumnsDefined.key]}-${header.id}-${index}`}
                      align={header.align}
                      padding="normal"
                      sx={{
                        minWidth: header.type !== 'action' ? width : '64px',
                        maxWidth: header.type !== 'action' ? width : '64px',
                      }}
                    >
                      <TableDataContent
                        tableColumnsDefined={tableColumnsDefined}
                        header={header}
                        rowData={row}
                        handleTableAction={handleTableAction}
                        handleOtherInfo={handleOtherInfo}
                      />
                    </TableCell>
                  )
                })
              }
            </TableRowDataStyle>
          )
        })
      }
    </>
  )
}

const TablePaginationActions = (props) => {
  const { count, page, rowsPerPage, onPageChange, searchKeyword, currentDataLength } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        aria-label="next page"
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        aria-label="last page"
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// 表格內容
const NormalTable = ({
  isPagination,
  userAuth,
  size,
  name,
  tableColumnsDefined,
  tableData,
  searchKeyword,
  isLoading,
  handleSettings,
  handleTableAction,
  handleOtherInfo,
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  isMoblie
}) => {
  const [sort, setSort] = useState(tableColumnsDefined.defaultSort);
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    if (!tableData.data) return;

    setSort(tableColumnsDefined.defaultSort);
    setOrder('desc');
    // setPage(0);
    // setRowsPerPage(10);
  }, [searchKeyword])

  const handleSettingChange = (settings) => {
    handleSettings({
      component: 'table',
      condition: settings
    })
  }

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = sort === property && order === 'asc';
    const curretnOrder = isAsc ? 'desc' : 'asc';
    setOrder(curretnOrder);
    setSort(property);
    setPage(0);

    handleSettingChange({
      rowsPerPage,
      sort: property,
      order: curretnOrder,
      page
    })
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);

    // handleSettingChange({
    //   rowsPerPage,
    //   sort,
    //   order,
    //   page: newPage
    // })
  };

  const handleChangeRowsPerPage = (event) => {
    if (searchKeyword) return;
    const rowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPerPage);
    setPage(0);

    handleSettingChange({
      sort,
      order,
      rowsPerPage,
      page: 0
    })
  };

  // console.log('tableData.data', tableData.data);

  return (
    <Box sx={{ position: 'relative' }}>
      <TableContainer
        sx={{
          minHeight: TABLE_HEAD_HEIGHT + TABLE_ROWS * TABLE_ROWS_HEIGHT_TWO - 5,
          maxHeight: TABLE_HEAD_HEIGHT + TABLE_ROWS * TABLE_ROWS_HEIGHT_TWO - 5,
          overflow: 'auto'
        }}
      >
        {
          tableData.data?.length > 0
          && (
            <Table
              size={size || 'medium'}
              stickyHeader={true}
            >
              <TableHead>
                <TableRow>

                  <CustomTableHeader
                    userAuth={userAuth}
                    sort={sort}
                    order={order}
                    tableColumnsDefined={tableColumnsDefined}
                    createSortHandler={createSortHandler}
                  />

                </TableRow>
              </TableHead>

              <TableBody>

                <CustomTableDataCell
                  userAuth={userAuth}
                  tableColumnsDefined={tableColumnsDefined}
                  tableData={tableData.data}
                  handleTableAction={handleTableAction}
                  handleOtherInfo={handleOtherInfo}
                />

              </TableBody>

            </Table>
          )
        }
        {
          !isLoading
          && tableData.data
          && !tableData.data?.length &&
          <NoData
            searchKeyword={searchKeyword}
          />
        }
      </TableContainer>
      {
        isPagination && tableData.data?.length > 0
        && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={tableData.page.size_total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={(props) => (
              <TablePaginationActions
                {...props}
                searchKeyword={searchKeyword}
                currentDataLength={tableData.data?.length || 0}
              />
            )}
            labelRowsPerPage={isMoblie ? "每頁：" : "每頁筆數"}
            labelDisplayedRows={({ from, to, count }) =>
              isMoblie
                ? `第 ${page + 1} 頁 / ${tableData.page.total_page} 頁`
                : `第 ${page + 1} 頁 / 共 ${tableData.page.total_page} 頁 (${count} 筆)`
            }
          />
        )
      }
    </Box>
  );
};


export default NormalTable;